import i18n from '@/i18n';
import { localeTag } from '@/i18n/format';
import { useCallback, useEffect, useRef } from 'react';
import { useMapStore } from '../store/map.store';
import { Draw, DragPan } from 'ol/interaction';
import DragBox from 'ol/interaction/DragBox';
import { always, shiftKeyOnly, noModifierKeys } from 'ol/events/condition';
import { getCenter as getExtentCenter, getBottomLeft, getTopRight, boundingExtent } from 'ol/extent';
import { Vector as VectorLayer } from 'ol/layer';
import { Vector as VectorSource } from 'ol/source';
import { GeoJSON } from 'ol/format';
import { Geometry, Point } from 'ol/geom';
import { Feature } from 'ol';
import TileWMS from 'ol/source/TileWMS';
import ImageWMS from 'ol/source/ImageWMS';
import { Style, Fill, Stroke, Icon, Circle as CircleStyle, Text } from 'ol/style';
import { Coordinate } from 'ol/coordinate';
import { toLonLat, fromLonLat } from 'ol/proj';
import { LineString } from 'ol/geom';
import { getDistance } from 'ol/sphere';
import { resetCatchmentLayerCache } from '../utils/catchmentRenderer';
import { getMapColors } from '../themes/mapTheme';

/** Convert a hex color string to an rgba string. */
function hexToRgba(hex: string, alpha: number): string {
  const bigint = parseInt(hex.replace('#', ''), 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r},${g},${b},${alpha})`;
}

/** Dig into a GeoJSON geometry and return the first [x,y] coordinate pair. */
function extractFirstCoord(geometry: any): [number, number] | null {
  if (!geometry?.coordinates) return null;
  let c = geometry.coordinates;
  while (Array.isArray(c) && Array.isArray(c[0])) c = c[0];
  if (Array.isArray(c) && typeof c[0] === 'number' && typeof c[1] === 'number') {
    return [c[0], c[1]];
  }
  return null;
}

/* ── Shared vector layer for drawings / info highlights ── */
let dispSource: VectorSource | null = null;
let dispLayer: VectorLayer<any> | null = null;

/** Reset module-level refs when the map is recreated */
export function resetInteractionRefs() {
  dispSource = null;
  dispLayer = null;
}

/** Clear all features from the shared display/highlight layer */
export function clearDispHighlights() {
  if (dispSource) dispSource.clear();
}

function getDispLayer(map: any): { source: VectorSource; layer: VectorLayer<any> } {
  // If cached layer exists, verify it's still on the current map
  if (dispSource && dispLayer) {
    let found = false;
    map.getLayers().forEach((l: any) => {
      if (l === dispLayer) found = true;
    });
    if (found) return { source: dispSource, layer: dispLayer };
    // Layer belongs to a disposed map, recreate
    dispSource = null;
    dispLayer = null;
  }

  const mc = getMapColors();
  const selColor = mc.selection;
  const selFill = hexToRgba(selColor, 0.25);

  dispSource = new VectorSource();
  dispLayer = new VectorLayer({
    source: dispSource,
    properties: { name: 'dispConfLayer' },
    style: new Style({
      fill: new Fill({ color: selFill }),
      stroke: new Stroke({ color: selColor, width: 2 }),
      image: new CircleStyle({
        radius: 6,
        fill: new Fill({ color: selColor }),
        stroke: new Stroke({ color: '#fff', width: 2 }),
      }),
    }),
    zIndex: 900,
  });
  map.addLayer(dispLayer);
  return { source: dispSource, layer: dispLayer };
}

/* ── Distance line layer (dedicated, not shared with dispSource) ── */

let distLineSource: VectorSource | null = null;
let distLineLayer: VectorLayer<any> | null = null;

function getDistLineLayer(map: any): VectorSource {
  if (distLineSource && distLineLayer) {
    let found = false;
    map.getLayers().forEach((l: any) => { if (l === distLineLayer) found = true; });
    if (found) return distLineSource;
  }
  distLineSource = new VectorSource();
  distLineLayer = new VectorLayer({
    source: distLineSource,
    properties: { name: 'distLineLayer' },
    zIndex: 910,
  });
  map.addLayer(distLineLayer);
  return distLineSource;
}

/* ── Cannibalization marker management ── */

let cannibMarkerSource: VectorSource | null = null;
let cannibMarkerLayer: VectorLayer<any> | null = null;

function getCannibMarkerLayer(map: any): VectorSource {
  if (cannibMarkerSource && cannibMarkerLayer) {
    let found = false;
    map.getLayers().forEach((l: any) => { if (l === cannibMarkerLayer) found = true; });
    if (found) return cannibMarkerSource;
  }
  cannibMarkerSource = new VectorSource();
  cannibMarkerLayer = new VectorLayer({
    source: cannibMarkerSource,
    properties: { name: 'cannibMarkerLayer' },
    zIndex: 950,
  });
  map.addLayer(cannibMarkerLayer);
  return cannibMarkerSource;
}

function makePinStyle(label: string, color: string): Style {
  return new Style({
    image: new Icon({
      anchor: [0.5, 1],
      src: `data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="28" height="40"><path d="M14 0C6.3 0 0 6.3 0 14c0 10.5 14 26 14 26s14-15.5 14-26C28 6.3 21.7 0 14 0zm0 20c-3.3 0-6-2.7-6-6s2.7-6 6-6 6 2.7 6 6-2.7 6-6 6z" fill="${color}"/></svg>`,
      scale: 1,
    }),
    text: new Text({
      text: label,
      font: 'bold 11px sans-serif',
      offsetY: -44,
      fill: new Fill({ color: '#ffffff' }),
      stroke: new Stroke({ color: '#000000', width: 3 }),
      padding: [2, 4, 2, 4],
    }),
  });
}

/**
 * Update cannibalization point markers on the map.
 * Call from AppLayout whenever point1 or point2 changes.
 * Points are [lon, lat] in EPSG:4326.
 */
export function updateCannibMarkers(
  map: any,
  point1: [number, number] | null,
  point2: [number, number] | null,
) {
  const source = getCannibMarkerLayer(map);

  // Remove existing cannib markers
  const toRemove = source.getFeatures().filter(
    (f) => f.get('_cannibId') === 1 || f.get('_cannibId') === 2,
  );
  toRemove.forEach((f) => source.removeFeature(f));

  // Add point 1
  if (point1) {
    const coord = fromLonLat(point1);
    const feat = new Feature({ geometry: new Point(coord) });
    feat.set('_cannibId', 1);
    feat.setStyle(makePinStyle('Point 1', '%233b82f6')); // blue
    source.addFeature(feat);
  }

  // Add point 2
  if (point2) {
    const coord = fromLonLat(point2);
    const feat = new Feature({ geometry: new Point(coord) });
    feat.set('_cannibId', 2);
    feat.setStyle(makePinStyle('Point 2', '%23e53e3e')); // red
    source.addFeature(feat);
  }
}

/**
 * Update a single labeled analysis marker (for catchment / ringBuffer).
 * Pass null to clear.
 */
export function updateAnalysisMarker(
  map: any,
  point: [number, number] | null,
  label = i18n.t('distance.pin_point'),
) {
  const source = getCannibMarkerLayer(map);

  // Remove existing analysis marker
  const toRemove = source.getFeatures().filter((f) => f.get('_analysisMarker'));
  toRemove.forEach((f) => source.removeFeature(f));

  if (point) {
    const coord = fromLonLat(point);
    const feat = new Feature({ geometry: new Point(coord) });
    feat.set('_analysisMarker', true);
    feat.setStyle(makePinStyle(label, '%23e53e3e')); // red
    source.addFeature(feat);
  }
}

/**
 * Update distance point markers on the map.
 * Call from AppLayout whenever start or end point changes.
 * Points are [lon, lat] in EPSG:4326.
 */
export function updateDistanceMarkers(
  map: any,
  startPt: [number, number] | null,
  endPt: [number, number] | null,
) {
  const source = getCannibMarkerLayer(map);

  // Remove existing distance markers
  const toRemove = source.getFeatures().filter(
    (f) => f.get('_distId') === 'start' || f.get('_distId') === 'end',
  );
  toRemove.forEach((f) => source.removeFeature(f));

  if (startPt) {
    const coord = fromLonLat(startPt);
    const feat = new Feature({ geometry: new Point(coord) });
    feat.set('_distId', 'start');
    feat.setStyle(makePinStyle(i18n.t('distance.pin_start'), '%2322c55e')); // green
    source.addFeature(feat);
  }

  if (endPt) {
    const coord = fromLonLat(endPt);
    const feat = new Feature({ geometry: new Point(coord) });
    feat.set('_distId', 'end');
    feat.setStyle(makePinStyle(i18n.t('distance.pin_end'), '%23e53e3e')); // red
    source.addFeature(feat);
  }
}

/** Clear distance markers and lines from the map */
export function clearDistanceMarkers(_map?: any) {
  // Clear marker pins
  if (cannibMarkerSource) {
    const toRemove = cannibMarkerSource.getFeatures().filter(
      (f) => f.get('_distId') === 'start' || f.get('_distId') === 'end',
    );
    toRemove.forEach((f) => cannibMarkerSource!.removeFeature(f));
  }
  // Clear distance lines
  if (distLineSource) {
    distLineSource.clear();
  }
}

/** Clear all analysis/cannibalization markers from the map */
export function clearCannibMarkers(map: any) {
  if (!cannibMarkerSource) return;
  let found = false;
  map.getLayers().forEach((l: any) => { if (l === cannibMarkerLayer) found = true; });
  if (found) {
    cannibMarkerSource.clear();
  }
}

/* ── Types (re-exported from FeatureInfoPanel) ── */
import type { RawFeature, FeatureInfoResult } from '../components/Info/FeatureInfoPanel';
export type { RawFeature, FeatureInfoResult };

interface MapInteractionCallbacks {
  onFeatureInfo?: (results: FeatureInfoResult[]) => void;
  onPointSelected?: (coord: Coordinate, lonLat: [number, number], feature?: Feature<Geometry>) => void;
  onPolygonDrawn?: (feature: Feature<Geometry>) => void;
  onPolyInfoClick?: (results: FeatureInfoResult[], extent: [number, number, number, number]) => void;
}

let _callbacks: MapInteractionCallbacks = {};

export function setInteractionCallbacks(cb: MapInteractionCallbacks) {
  _callbacks = { ..._callbacks, ...cb };
}

export const useMapInteractions = (options: MapInteractionCallbacks = {}) => {
  const { mapInstance: map, currentTool, setCurrentTool } = useMapStore();
  const interactionsRef = useRef<any[]>([]);
  const callbacksRef = useRef(options);
  callbacksRef.current = options;

  /*
   * Persistent DragBox interactions for area zoom — mirrors legacy control.manager.js.
   * Uses DragBox (not DragZoom) with manual view.fit() in boxend handler,
   * because OL9 DragZoom.fitInternal() doesn't work correctly with constrained views.
   */
  const dragZoomInRef = useRef<DragBox | null>(null);
  const dragZoomOutRef = useRef<DragBox | null>(null);

  useEffect(() => {
    if (!map || dragZoomInRef.current) return;

    /*
     * Helper: snap a raw resolution to the nearest integer zoom level.
     * Uses getZoomForResolution + getResolutionForZoom which are stable
     * public APIs in OL 9.x through 10.x (avoids getConstrainedResolution
     * which was removed in v10).
     */
    const snapResolution = (rawRes: number) => {
      const view = map.getView();
      const zoom = Math.round(view.getZoomForResolution(rawRes) ?? 0);
      return view.getResolutionForZoom(zoom);
    };

    /* ── Zoom In: mirrors legacy dragzoominteraction.js onBoxEnd (out_=false) ── */
    const dzIn = new DragBox({ condition: always, className: 'ol-dragzoom' });
    dzIn.on('boxend', () => {
      const view = map.getView();
      const size = map.getSize();
      if (!size) return;

      const geom = dzIn.getGeometry();
      if (!geom) return;

      const extent = geom.getExtent();
      const resolution = snapResolution(view.getResolutionForExtent(extent, size));
      const center = getExtentCenter(extent);

      view.cancelAnimations();
      view.setCenter(center);
      view.setResolution(resolution);
    });
    dzIn.setActive(false);
    map.addInteraction(dzIn);
    dragZoomInRef.current = dzIn;

    /* ── Zoom Out: mirrors legacy dragzoominteraction.js onBoxEnd (out_=true) ── */
    const dzOut = new DragBox({ condition: always, className: 'ol-dragzoom' });
    dzOut.on('boxend', () => {
      const view = map.getView();
      const size = map.getSize();
      if (!size) return;

      const geom = dzOut.getGeometry();
      if (!geom) return;

      const boxExtent = geom.getExtent();
      const mapExtent = view.calculateExtent(size);

      const blPixel = map.getPixelFromCoordinate(getBottomLeft(boxExtent));
      const trPixel = map.getPixelFromCoordinate(getTopRight(boxExtent));
      if (!blPixel || !trPixel) return;
      const pixelExtent = boundingExtent([blPixel, trPixel]);

      const factor = view.getResolutionForExtent(pixelExtent, size);

      const cx = (mapExtent[0] + mapExtent[2]) / 2;
      const cy = (mapExtent[1] + mapExtent[3]) / 2;
      const hw = ((mapExtent[2] - mapExtent[0]) / 2) * (1 / factor);
      const hh = ((mapExtent[3] - mapExtent[1]) / 2) * (1 / factor);
      const scaledExtent: [number, number, number, number] = [cx - hw, cy - hh, cx + hw, cy + hh];

      const resolution = snapResolution(view.getResolutionForExtent(scaledExtent, size));
      const center = getExtentCenter(scaledExtent);

      view.cancelAnimations();
      view.setCenter(center);
      view.setResolution(resolution);
    });
    dzOut.setActive(false);
    map.addInteraction(dzOut);
    dragZoomOutRef.current = dzOut;

    return () => {
      map.removeInteraction(dzIn);
      map.removeInteraction(dzOut);
      dragZoomInRef.current = null;
      dragZoomOutRef.current = null;
    };
  }, [map]);

  /* ── Remove all custom interactions ── */
  const removeInteractions = useCallback(() => {
    if (!map) return;
    interactionsRef.current.forEach((i) => {
      try { map.removeInteraction(i); } catch { /* already removed */ }
    });
    interactionsRef.current = [];

    // Deactivate persistent DragZoom interactions
    dragZoomInRef.current?.setActive(false);
    dragZoomOutRef.current?.setActive(false);

    // Re-enable default DragPan
    map.getInteractions().forEach((i: any) => {
      if (i instanceof DragPan) i.setActive(true);
    });
  }, [map]);

  /* ── Click handler for Feature Info tool ── */
  useEffect(() => {
    if (!map) return;

    const clickHandler = (evt: any) => {
      const tool = useMapStore.getState().currentTool;
      if (tool !== 'info' && tool !== 'multiInfo' && tool !== 'polyInfo') return;

      const view = map.getView();
      const resolution = view.getResolution();
      if (!resolution) return;

      const { source } = getDispLayer(map);
      // In multiInfo mode, keep previous highlights (accumulate); in other modes clear
      if (tool !== 'multiInfo') {
        source.clear();
      }

      const allResults: FeatureInfoResult[] = [];
      let pending = 0;

      // The company's own layers are drawn in the browser: what is under the click is read from them directly.
      map.forEachFeatureAtPixel(evt.pixel, (feature: any, layer: any) => {
        const layerName = layer?.get('title') || layer?.get('name') || 'Layer';
        const geometry = feature.getGeometry();
        const props = { ...feature.getProperties() };
        delete props.geometry;
        let geo: any = null;
        if (geometry?.getType() === 'Point') {
          geo = { type: 'Point', coordinates: toLonLat(geometry.getCoordinates(), 'EPSG:3857') };
        }
        const entry = allResults.find((r) => r.layerName === layerName);
        const raw: RawFeature = { type: 'Feature', id: feature.getId(), geometry: geo, properties: props };
        if (entry) entry.features.push(raw);
        else allResults.push({ layerName, features: [raw] });
      }, { layerFilter: (l: any) => !!l.get('companyLayer'), hitTolerance: 4 });

      // Query every visible WMS layer (ImageWMS or TileWMS)
      map.getLayers().forEach((layer: any) => {
        if (!layer.getVisible()) return;
        const wmsSource = layer.getSource?.();
        if (!wmsSource || (!(wmsSource instanceof TileWMS) && !(wmsSource instanceof ImageWMS))) return;

        const url = wmsSource.getFeatureInfoUrl(
          evt.coordinate,
          resolution,
          'EPSG:3857',
          { INFO_FORMAT: 'application/json', FEATURE_COUNT: 500 },
        );
        if (!url) return;

        pending++;
        fetch(url)
          .then((r) => r.json())
          .then((data) => {
            if (data?.features?.length) {
              const layerName = layer.get('name') || 'Unknown';
              const features: RawFeature[] = data.features.map((f: any) => {
                // Convert geometry coordinates from EPSG:3857 → EPSG:4326 (WGS84)
                // so downstream code (catchment) gets proper lat/lon
                let geometry = f.geometry;
                if (geometry?.type === 'Point' && Array.isArray(geometry.coordinates)) {
                  // GeoServer answers in the layer's own CRS or in the request's: only metres are converted,
                  // a point already in degrees (most of our layers) is kept as it is.
                  const [x, y] = geometry.coordinates as number[];
                  if (Math.abs(x) > 180 || Math.abs(y) > 90) {
                    const [lon, lat] = toLonLat([x, y], 'EPSG:3857');
                    geometry = { ...geometry, coordinates: [lon, lat] };
                  }
                }
                return {
                  type: f.type || 'Feature',
                  id: f.id,
                  geometry,
                  properties: f.properties || {},
                };
              });

              // Highlight polygon features on the map (legacy behaviour)
              // GeoServer GetFeatureInfo returns geometry in native CRS (usually EPSG:4326)
              const hlGeoJson = new GeoJSON();
              data.features.forEach((f: any) => {
                try {
                  const geomType = (f.geometry?.type || '').toLowerCase();
                  if (!geomType.includes('polygon')) return; // only polygons

                  // Detect projection from coordinate magnitude:
                  // EPSG:4326 coords are -180..180 / -90..90, EPSG:3857 coords are in millions
                  const firstCoord = extractFirstCoord(f.geometry);
                  const isLonLat = firstCoord && Math.abs(firstCoord[0]) <= 180 && Math.abs(firstCoord[1]) <= 90;
                  const dataCrs = isLonLat ? 'EPSG:4326' : 'EPSG:3857';

                  const result = hlGeoJson.readFeature(f, {
                    dataProjection: dataCrs,
                    featureProjection: 'EPSG:3857',
                  });
                  // OL10 readFeature may return a single Feature or an array
                  const feats = Array.isArray(result) ? result : [result];
                  feats.forEach((olF) => { if (olF) source.addFeature(olF); });
                } catch { /* skip */ }
              });

              allResults.push({ layerName, features });
            }
          })
          .catch(() => { /* ignore failed layers */ })
          .finally(() => {
            pending--;
            if (pending <= 0) {
              if (tool === 'polyInfo') {
                // For polyInfo, compute extent from the first polygon's geometry.
                // GeoServer may return coords in native CRS (usually EPSG:4326) or EPSG:3857.
                // We detect from coordinate magnitude and always output EPSG:4326 extent.
                let extent: [number, number, number, number] = [0, 0, 0, 0];
                let found = false;
                for (const result of allResults) {
                  if (found) break;
                  for (const feat of result.features) {
                    if (!feat.geometry?.type?.toLowerCase().includes('polygon')) continue;
                    try {
                      const coords: number[][] = [];
                      const extractCoords2 = (c: any) => {
                        if (Array.isArray(c) && typeof c[0] === 'number') {
                          coords.push(c as number[]);
                        } else if (Array.isArray(c)) {
                          c.forEach(extractCoords2);
                        }
                      };
                      extractCoords2(feat.geometry.coordinates);
                      if (coords.length === 0) continue;

                      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
                      coords.forEach(([x, y]) => {
                        if (x < minX) minX = x;
                        if (y < minY) minY = y;
                        if (x > maxX) maxX = x;
                        if (y > maxY) maxY = y;
                      });

                      // Detect if coordinates are in EPSG:3857 (large values) or EPSG:4326
                      const isEpsg3857 = Math.abs(minX) > 180 || Math.abs(maxX) > 180 || Math.abs(minY) > 90 || Math.abs(maxY) > 90;
                      if (isEpsg3857) {
                        // Transform corners from 3857 → 4326
                        const bl = toLonLat([minX, minY], 'EPSG:3857');
                        const tr = toLonLat([maxX, maxY], 'EPSG:3857');
                        extent = [bl[0], bl[1], tr[0], tr[1]]; // [minLon, minLat, maxLon, maxLat]
                      } else {
                        extent = [minX, minY, maxX, maxY]; // already in 4326
                      }
                      found = true;
                    } catch { /* skip */ }
                    break;
                  }
                }
                callbacksRef.current.onPolyInfoClick?.(allResults, extent);
                _callbacks.onPolyInfoClick?.(allResults, extent);
              } else {
                callbacksRef.current.onFeatureInfo?.(allResults);
                _callbacks.onFeatureInfo?.(allResults);
              }
            }
          });
      });

      // If no WMS layers visible, send empty result
      if (pending === 0) {
        if (tool === 'polyInfo') {
          callbacksRef.current.onPolyInfoClick?.([], [0, 0, 0, 0]);
          _callbacks.onPolyInfoClick?.([], [0, 0, 0, 0]);
        } else {
          callbacksRef.current.onFeatureInfo?.(allResults);
          _callbacks.onFeatureInfo?.(allResults);
        }
      }
    };

    /* Street View click handler — fires onPointSelected for streetView tool */
    const streetViewClickHandler = (evt: any) => {
      const tool = useMapStore.getState().currentTool;
      if (tool !== 'streetView') return;
      const coord = evt.coordinate;
      const lonLat = toLonLat(coord, 'EPSG:3857') as [number, number];
      callbacksRef.current.onPointSelected?.(coord, lonLat);
      _callbacks.onPointSelected?.(coord, lonLat);
    };

    map.on('singleclick', clickHandler);
    map.on('singleclick', streetViewClickHandler);
    return () => {
      map.un('singleclick', clickHandler);
      map.un('singleclick', streetViewClickHandler);
    };
  }, [map]);

  /* ── Activate tool ── */
  const activateTool = useCallback(
    (toolName: string) => {
      if (!map) return;
      removeInteractions();
      const { source } = getDispLayer(map);

      switch (toolName) {
        /* ─── Drawing tools ─── */
        case 'drawPolygon':
        case 'addPolygon': {
          const draw = new Draw({ source, type: 'Polygon' });
          draw.on('drawend', (evt) => {
            callbacksRef.current.onPolygonDrawn?.(evt.feature);
            _callbacks.onPolygonDrawn?.(evt.feature);
          });
          map.addInteraction(draw);
          interactionsRef.current.push(draw);
          break;
        }

        case 'addLine': {
          const draw = new Draw({ source, type: 'LineString' });
          draw.on('drawend', (evt) => {
            callbacksRef.current.onPolygonDrawn?.(evt.feature);
            _callbacks.onPolygonDrawn?.(evt.feature);
          });
          map.addInteraction(draw);
          interactionsRef.current.push(draw);
          break;
        }

        case 'areaAnalysis': {
          // Clear previous drawings before starting new area analysis
          source.clear();
          // Polygon draw with freehand mode when Shift is held
          const draw = new Draw({
            source,
            type: 'Polygon',
            freehandCondition: shiftKeyOnly,
            condition: (e) => {
              // Allow normal clicks when Shift is NOT held
              return noModifierKeys(e) || shiftKeyOnly(e);
            },
          });
          draw.on('drawend', (evt) => {
            // Clear previous polygon before adding the new one
            source.clear();
            callbacksRef.current.onPolygonDrawn?.(evt.feature);
            _callbacks.onPolygonDrawn?.(evt.feature);
          });
          map.addInteraction(draw);
          interactionsRef.current.push(draw);
          break;
        }

        case 'drawPoint':
        case 'addPoint': {
          const draw = new Draw({ source, type: 'Point' });
          draw.on('drawend', (evt) => {
            const geom = evt.feature.getGeometry() as Point;
            const coord = geom.getCoordinates();
            const lonLat = toLonLat(coord, 'EPSG:3857') as [number, number];

            // Add a marker icon
            evt.feature.setStyle(new Style({
              image: new Icon({
                anchor: [0.5, 1],
                src: 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="24" height="36"><path d="M12 0C5.4 0 0 5.4 0 12c0 9 12 24 12 24s12-15 12-24C24 5.4 18.6 0 12 0zm0 18c-3.3 0-6-2.7-6-6s2.7-6 6-6 6 2.7 6 6-2.7 6-6 6z" fill="%23e53e3e"/></svg>',
                scale: 1,
              }),
            }));

            callbacksRef.current.onPointSelected?.(coord, lonLat, evt.feature);
            _callbacks.onPointSelected?.(coord, lonLat, evt.feature);
          });
          map.addInteraction(draw);
          interactionsRef.current.push(draw);
          break;
        }

        case 'catchment':
        case 'ringBuffer': {
          // Markers managed externally via updateAnalysisMarker — no marker styling here
          const draw = new Draw({ source, type: 'Point' });
          draw.on('drawend', (evt) => {
            const geom = evt.feature.getGeometry() as Point;
            const coord = geom.getCoordinates();
            const lonLat = toLonLat(coord, 'EPSG:3857') as [number, number];

            // Remove raw draw feature — marker managed separately
            setTimeout(() => source.removeFeature(evt.feature), 0);

            callbacksRef.current.onPointSelected?.(coord, lonLat);
            _callbacks.onPointSelected?.(coord, lonLat);
          });
          map.addInteraction(draw);
          interactionsRef.current.push(draw);
          break;
        }

        case 'cannibalization': {
          // Cannibalization uses its own labeled marker layer (managed by updateCannibMarkers)
          // The Draw interaction only fires the callback — no marker styling here
          const draw = new Draw({ source, type: 'Point' });
          draw.on('drawend', (evt) => {
            const geom = evt.feature.getGeometry() as Point;
            const coord = geom.getCoordinates();
            const lonLat = toLonLat(coord, 'EPSG:3857') as [number, number];

            // Remove the raw draw feature — markers are managed separately
            setTimeout(() => source.removeFeature(evt.feature), 0);

            callbacksRef.current.onPointSelected?.(coord, lonLat);
            _callbacks.onPointSelected?.(coord, lonLat);
          });
          map.addInteraction(draw);
          interactionsRef.current.push(draw);
          break;
        }

        case 'distance': {
          // Markers managed externally via updateDistanceMarkers — no marker styling here
          const draw = new Draw({ source, type: 'Point' });
          draw.on('drawend', (evt) => {
            const geom = evt.feature.getGeometry() as Point;
            const coord = geom.getCoordinates();
            const lonLat = toLonLat(coord, 'EPSG:3857') as [number, number];

            // Remove the raw draw feature — markers are managed separately
            setTimeout(() => source.removeFeature(evt.feature), 0);

            callbacksRef.current.onPointSelected?.(coord, lonLat);
            _callbacks.onPointSelected?.(coord, lonLat);
          });
          map.addInteraction(draw);
          interactionsRef.current.push(draw);
          break;
        }

        case 'measure': {
          const measureSource = new VectorSource();
          const measureLayer = new VectorLayer({
            source: measureSource,
            properties: { name: 'measureLayer' },
            style: new Style({
              stroke: new Stroke({ color: '#ff6600', width: 3 }),
              fill: new Fill({ color: 'rgba(255,102,0,0.1)' }),
            }),
            zIndex: 950,
          });
          map.addLayer(measureLayer);

          const draw = new Draw({ source: measureSource, type: 'LineString' });
          draw.on('drawend', (evt) => {
            const geom = evt.feature.getGeometry();
            if (geom) {
              const length = (geom as any).getLength?.() || 0;
              const km = (length / 1000).toLocaleString(localeTag(), { minimumFractionDigits: 2, maximumFractionDigits: 2 });
              alert(i18n.t('distance.measured', { km }));
            }
          });
          map.addInteraction(draw);
          interactionsRef.current.push(draw);
          break;
        }

        /* ─── Zoom tools — persistent DragZoom toggled via setActive ─── */
        /*
         * Mirrors legacy control.manager.js approach: interactions are added
         * to the map once at init with setActive(false), then toggled on/off.
         * Default DragPan is disabled while DragZoom is active to prevent conflicts.
         */
        case 'zoomInArea': {
          map.getInteractions().forEach((i: any) => {
            if (i instanceof DragPan) i.setActive(false);
          });
          dragZoomOutRef.current?.setActive(false);
          dragZoomInRef.current?.setActive(true);
          break;
        }

        case 'zoomOutArea': {
          map.getInteractions().forEach((i: any) => {
            if (i instanceof DragPan) i.setActive(false);
          });
          dragZoomInRef.current?.setActive(false);
          dragZoomOutRef.current?.setActive(true);
          break;
        }

        /* ─── Pan, info, streetView, polyInfo — click-based, no OL interaction needed ─── */
        case 'pan':
        case 'info':
        case 'streetView':
        case 'multiInfo':
        case 'editStyle':
        case 'filter':
        case 'geocoder':
        case 'polyInfo':
          break;

        default:
          break;
      }

      setCurrentTool(toolName as Parameters<typeof setCurrentTool>[0]);
    },
    [map, removeInteractions, setCurrentTool],
  );

  /* ── Deactivate ── */
  const deactivateTool = useCallback(() => {
    removeInteractions();
    setCurrentTool(null);
  }, [removeInteractions, setCurrentTool]);

  /* ── Clear drawings ── */
  const clearDrawings = useCallback(() => {
    if (!map) return;
    // Clear dispConfLayer
    if (dispSource) dispSource.clear();
    // Clear distance lines
    if (distLineSource) distLineSource.clear();

    // Collect catchment layers to remove (created dynamically by catchmentRenderer)
    const toRemove: any[] = [];

    map.getLayers().forEach((layer: any) => {
      const name = layer.get?.('name') || '';
      // Utility draw/measure layers — clear features
      if (['drawLayer', 'measureLayer', 'dispConfLayer'].includes(name)) {
        const src = layer.getSource?.();
        if (src?.clear) src.clear();
      }
      // MapView config layers (walk1-5, drive1-5, motor1-5, RingBuffer)
      if (/^(walk|drive|motor)\d+$/.test(name) || name === 'RingBuffer') {
        const src = layer.getSource?.();
        if (src?.clear) src.clear();
        layer.setVisible(false);
      }
      // catchmentRenderer layers (1stWalkinLayer, 2ndDriveLayer, etc.)
      if (layer.get?.('catchmentLayer')) {
        const src = layer.getSource?.();
        if (src?.clear) src.clear();
        toRemove.push(layer);
      }
    });

    // Remove dynamic catchment layers from the map entirely
    toRemove.forEach((l) => map.removeLayer(l));
    // Reset catchmentRenderer's internal cache so it can recreate layers fresh
    resetCatchmentLayerCache();
  }, [map]);

  /* ── Export geometry ── */
  const exportGeometry = useCallback(
    (format: 'geojson' | 'wkt' = 'geojson') => {
      if (!dispSource) return null;
      const features = dispSource.getFeatures();
      if (format === 'geojson') {
        return new GeoJSON().writeFeaturesObject(features);
      }
      return features.map((f) => f.getGeometry()?.toString()).filter(Boolean);
    },
    [],
  );

  /* ── Download map as PNG (with legend overlay) ── */
  const downloadMapPng = useCallback(() => {
    if (!map) return;
    map.once('rendercomplete', () => {
      const mapCanvas = document.createElement('canvas');
      const size = map.getSize()!;
      mapCanvas.width = size[0];
      mapCanvas.height = size[1];
      const ctx = mapCanvas.getContext('2d')!;

      // Draw map layers
      document.querySelectorAll('.ol-layer canvas').forEach((canvas) => {
        const el = canvas as HTMLCanvasElement;
        if (el.width > 0) {
          const opacity = (el.parentNode as HTMLElement)?.style?.opacity || '1';
          ctx.globalAlpha = parseFloat(opacity);
          const transform = el.style.transform;
          const match = transform.match(/translate\((-?\d+(?:\.\d+)?)px,\s*(-?\d+(?:\.\d+)?)px\)/);
          if (match) {
            ctx.drawImage(el, parseFloat(match[1]), parseFloat(match[2]));
          } else {
            ctx.drawImage(el, 0, 0);
          }
          ctx.globalAlpha = 1;
        }
      });

      // Draw legend overlay onto the canvas
      try {
        drawSimpleLegend(ctx, size[1]);
      } catch {
        // If legend rendering fails, just download the map without it
      }

      mapCanvas.toBlob((blob) => {
        if (blob) {
          const link = document.createElement('a');
          link.download = 'map.png';
          link.href = URL.createObjectURL(blob);
          link.click();
          URL.revokeObjectURL(link.href);
        }
      });
    });
    map.renderSync();
  }, [map]);

  /** Simple fallback legend drawing when html2canvas is unavailable */
  function drawSimpleLegend(ctx: CanvasRenderingContext2D, canvasHeight: number) {
    if (!map) return;
    const visibleLayers: string[] = [];
    map.getLayers().forEach((layer: any) => {
      if (layer.getVisible() && layer.get('name')) {
        visibleLayers.push(layer.get('name'));
      }
    });
    if (visibleLayers.length === 0) return;

    const padding = 10;
    const lineHeight = 18;
    const boxW = 200;
    const boxH = padding * 2 + 20 + visibleLayers.length * lineHeight;
    const x = 12;
    const y = canvasHeight - boxH - 12;

    // Background
    ctx.fillStyle = 'rgba(17, 24, 39, 0.85)';
    ctx.strokeStyle = 'rgba(55, 65, 81, 0.5)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.roundRect(x, y, boxW, boxH, 6);
    ctx.fill();
    ctx.stroke();

    // Title
    ctx.fillStyle = '#e5e7eb';
    ctx.font = 'bold 12px sans-serif';
    ctx.fillText(i18n.t('map.legend'), x + padding, y + padding + 12);

    // Layer entries
    ctx.font = '11px sans-serif';
    ctx.fillStyle = '#9ca3af';
    visibleLayers.forEach((name, i) => {
      const ly = y + padding + 24 + i * lineHeight;
      // Color swatch
      ctx.fillStyle = `hsl(${(i * 47) % 360}, 60%, 50%)`;
      ctx.fillRect(x + padding, ly, 10, 10);
      // Label
      ctx.fillStyle = '#9ca3af';
      ctx.fillText(name, x + padding + 16, ly + 9);
    });
  }

  /**
   * Draw distance lines between two lon/lat points on the map.
   * For bird's eye: straight dashed blue line.
   * For walking/driving: if routeCoords are provided, draw the actual route; otherwise straight line.
   * routeCoords are [lng, lat][] arrays (EPSG:4326).
   */
  const drawDistanceLine = useCallback(
    (
      startLonLat: [number, number],
      endLonLat: [number, number],
      modes?: { walk: boolean; drive: boolean; bird: boolean },
      routes?: { walkCoords?: [number, number][]; driveCoords?: [number, number][] },
    ) => {
      if (!map) return;
      // Use dedicated distance line layer (not shared dispSource)
      const source = getDistLineLayer(map);
      // Clear any previous distance lines
      source.clear();

      const c1 = fromLonLat(startLonLat, 'EPSG:3857');
      const c2 = fromLonLat(endLonLat, 'EPSG:3857');

      // Bird's eye: dashed blue straight line
      if (!modes || modes.bird) {
        const birdLine = new Feature({ geometry: new LineString([c1, c2]) });
        birdLine.setStyle(new Style({
          stroke: new Stroke({ color: '#3b82f6', width: 2, lineDash: [8, 6] }),
        }));
        source.addFeature(birdLine);
      }

      // Walking: solid green line (actual route if available)
      if (modes?.walk) {
        const coords = routes?.walkCoords
          ? routes.walkCoords.map((c) => fromLonLat(c, 'EPSG:3857'))
          : [c1, c2];
        const walkLine = new Feature({ geometry: new LineString(coords) });
        walkLine.setStyle(new Style({
          stroke: new Stroke({ color: '#22c55e', width: 4, lineCap: 'round', lineJoin: 'round' }),
        }));
        source.addFeature(walkLine);
      }

      // Driving: blue line (actual route if available)
      if (modes?.drive) {
        const coords = routes?.driveCoords
          ? routes.driveCoords.map((c) => fromLonLat(c, 'EPSG:3857'))
          : [c1, c2];
        const driveLine = new Feature({ geometry: new LineString(coords) });
        driveLine.setStyle(new Style({
          stroke: new Stroke({ color: '#ef4444', width: 4, lineCap: 'round', lineJoin: 'round' }),
        }));
        source.addFeature(driveLine);
      }
    },
    [map],
  );

  /* ── Calculate geodesic distance between two lon/lat points ── */
  const calculateGeodesicDistance = useCallback(
    (startLonLat: [number, number], endLonLat: [number, number]) => {
      // getDistance returns meters using Haversine formula
      return getDistance(startLonLat, endLonLat);
    },
    [],
  );

  return {
    activateTool,
    deactivateTool,
    currentTool,
    clearDrawings,
    exportGeometry,
    downloadMapPng,
    drawDistanceLine,
    calculateGeodesicDistance,
  };
};
